import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { GED_EXAM_SUBJECTS, formatUsd } from '../data/studyPlans';
import { notifyAdminPayment, notifyStudentPayment } from './notifications';

/** Fields a Meezan-style hosted checkout must never persist. */
const FORBIDDEN_PAYMENT_KEYS = [
  'studentName',
  'email',
  'registrationNumber',
  'cardLast4',
  'cardNumber',
  'cardName',
  'cvc',
  'cvv',
  'expiry',
  'pan',
  'nameOnCard',
];

export function generateReferenceNumber() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `SBA-${y}${m}${day}-${rand}`;
}

export function subjectNameById(id) {
  return GED_EXAM_SUBJECTS.find((s) => s.id === id)?.name || id;
}

/**
 * Order record for a hosted bank checkout.
 * Stores the order only: reference, account id, amount, status, and plan.
 * Never stores card data or the customer's name, email, or registration number.
 */
export async function createPaymentRecord({
  userId = '',
  category,
  label,
  amount,
  breakdown = null,
  mode = 'hosted-bank',
  status = 'paid',
  bankReference = '',
  subjects = [],
  paymentDate = null,
  createdBy = 'student-hosted',
  createdByUid = '',
  currency = 'USD',
  planId = '',
  days = 0,
}) {
  const referenceNumber = generateReferenceNumber();
  const payDate = paymentDate instanceof Date ? paymentDate : paymentDate ? new Date(paymentDate) : new Date();
  const total = Math.round((Number(amount) || 0) * 100) / 100;
  const safeMode = mode === 'cash' || mode === 'online' ? mode : 'hosted-bank';
  const safeRef = String(bankReference || '').trim();
  if (/^\d{12,19}$/.test(safeRef.replace(/\s/g, ''))) {
    throw new Error('Bank reference cannot be a card number.');
  }

  const payload = {
    referenceNumber,
    userId: userId || '',
    category: category || 'manual',
    label: (label || 'Payment').trim(),
    amount: total,
    currency,
    breakdown: breakdown || {
      items: [{ name: label || 'Payment', amount: total }],
      serviceAndTax: 0,
      subtotal: total,
    },
    mode: safeMode,
    gateway: 'hosted-bank',
    status: status || 'paid',
    bankReference: safeRef,
    subjects: Array.isArray(subjects) ? subjects.filter(Boolean) : [],
    paymentDate: Timestamp.fromDate(payDate),
    createdAt: serverTimestamp(),
    createdBy: createdBy || 'student-hosted',
    createdByUid: createdByUid || userId || '',
    planId: planId || '',
    days: Number(days) || 0,
    emailSent: false,
  };

  for (const key of FORBIDDEN_PAYMENT_KEYS) {
    delete payload[key];
  }

  const ref = await addDoc(collection(db, 'payments'), payload);

  const methodName = modeLabel(safeMode);
  if (userId) {
    await notifyStudentPayment({
      userId,
      amount: total,
      planLabel: payload.label,
      method: methodName,
      referenceNumber,
    }).catch(console.warn);
  }

  await notifyAdminPayment({
    amount: total,
    referenceNumber,
    method: methodName,
  }).catch(console.warn);

  return { id: ref.id, ...payload, paymentDate: payDate };
}

export async function getPaymentById(paymentId) {
  if (!paymentId) return null;
  const snap = await getDoc(doc(db, 'payments', paymentId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export function subscribeStudentPayments(userId, callback) {
  if (!userId) {
    callback([]);
    return () => {};
  }
  const q = query(collection(db, 'payments'), where('userId', '==', userId));
  return onSnapshot(
    q,
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => paymentDateMs(b) - paymentDateMs(a));
      callback(rows);
    },
    (err) => {
      console.error('Student payments load failed:', err);
      callback([]);
    }
  );
}

export function subscribeAllPayments(callback) {
  const q = query(collection(db, 'payments'), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    async (err) => {
      console.warn('All payments ordered query failed, falling back:', err?.message || err);
      try {
        const snap = await getDocs(collection(db, 'payments'));
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => paymentDateMs(b) - paymentDateMs(a));
        callback(rows);
      } catch (e) {
        console.error(e);
        callback([]);
      }
    }
  );
}

export function paymentDateMs(payment) {
  const raw = payment?.paymentDate;
  if (!raw) return 0;
  if (typeof raw.toDate === 'function') return raw.toDate().getTime();
  if (raw instanceof Date) return raw.getTime();
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

export function formatPaymentDate(payment) {
  const ms = paymentDateMs(payment);
  if (!ms) return '—';
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function categoryLabel(category) {
  switch (category) {
    case 'course':
      return 'Course / Admission';
    case 'exam':
      return 'GED Exam Fee';
    case 'degree':
      return 'Degree / Diploma';
    case 'extension':
      return 'Plan Extension';
    case 'manual':
      return 'Manual Entry';
    default:
      return category || 'Payment';
  }
}

export function modeLabel(mode) {
  switch (mode) {
    case 'card':
      return 'Hosted bank checkout';
    case 'hosted-bank':
      return 'Hosted bank checkout';
    case 'online':
      return 'Online Banking';
    case 'cash':
      return 'Cash';
    default:
      return mode || '—';
  }
}

/** Client-side search + sort. Includes registration number. */
export function filterAndSortPayments(payments, searchText) {
  const q = String(searchText || '')
    .trim()
    .toLowerCase();
  let list = Array.isArray(payments) ? [...payments] : [];
  if (q) {
    list = list.filter((p) => {
      const hay = [
        p.referenceNumber,
        p.label,
        p.userId,
        categoryLabel(p.category),
        modeLabel(p.mode),
        p.bankReference,
        formatUsd(p.amount),
        ...(p.subjects || []).map(subjectNameById),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }
  list.sort((a, b) => paymentDateMs(b) - paymentDateMs(a));
  return list;
}

/** Filter payments by inclusive date range (ms). */
export function filterPaymentsByDateRange(payments, fromMs, toMs) {
  let list = Array.isArray(payments) ? [...payments] : [];
  if (fromMs != null) {
    list = list.filter((p) => paymentDateMs(p) >= fromMs);
  }
  if (toMs != null) {
    list = list.filter((p) => paymentDateMs(p) <= toMs);
  }
  return list;
}

export function buildExamBreakdown(selectedSubjectIds, examFee) {
  const fee = examFee || {};
  const perFee = Number(fee.perSubjectUsd) || 0;
  const perTax = Number(fee.serviceAndTaxUsd) || 0;
  const items = [];
  let serviceAndTax = 0;
  let subtotal = 0;
  for (const id of selectedSubjectIds) {
    const name = subjectNameById(id);
    // Exam fee lines only — service/tax shown once at the end of the receipt
    items.push({ name: `${name} — exam fee`, amount: perFee });
    subtotal += perFee;
    serviceAndTax += perTax;
  }
  return {
    items,
    subtotal,
    serviceAndTax,
    total: subtotal + serviceAndTax,
  };
}

export function buildDegreeBreakdown(degreeFee) {
  const fee = degreeFee || {};
  const diploma = Number(fee.diplomaTranscriptUsd) || 0;
  const tax = Number(fee.serviceAndTaxUsd) || 0;
  return {
    items: [{ name: 'Diploma & transcript', amount: diploma }],
    subtotal: diploma,
    serviceAndTax: tax,
    total: diploma + tax,
  };
}
